import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import Router from 'next/router';
import Link from 'next/link';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Row, Col, Navbar, Button, NavbarToggler, Tooltip } from 'reactstrap';
import domainModel from 'domain-model';
import Image from 'flaivy-react/components/Image';
import CartOffCanvas from 'flaivy-react/components/cart/CartOffCanvas';
import StyledButton from 'flaivy-react/components/StyledButton';
import UserOffCanvas from './UserOffCanvas';
import LoginModal from '../LoginModal';
import useHamburgerMenu from './hamburgerMenuHook';
import PageNavigation from './PageNavigation';
import OnlyOnLargeScreen from './OnlyOnLargeScreen';
import OnlyOnSmallScreen from './OnlyOnSmallScreen';

import SearchInput from './SearchInput';

import IconHambugerMenu from '../icons/IconHamburgerMenu';
import IconProfile from '../icons/IconProfile';
import IconCart from '../icons/IconCart';
import MyFavoritesButton from './MyFavoritesButton';

import { toggleLoginModal as _toggleLoginModal } from '../../shared/actions/login-modal.action';
import keyedDebounceSingleton from 'flaivy-react/utils/keyed-debounce-singleton';

import MobileOffCanvas from './MobileOffCanvas';

const { isPrivabShop } = domainModel.shopConfig;

const Logo = ({ shopConfig, className }) => {
  const { logo, organization } = shopConfig;
  return (
    <>
      <Link href="/" className={`d-inline-block me-0 ${className}`}>
        <Image
          src={logo}
          alt={`${organization.name} Shop logo`}
          resX={175}
          resY={70}
          style={{ height: 'auto', maxWidth: '90%' }}
        />
      </Link>
    </>
  );
};

Logo.propTypes = {
  shopConfig: PropTypes.object.isRequired,
  className: PropTypes.string,
};
Logo.defaultProps = {
  className: '',
};

const ShoppingCartButton = ({
  persistedMultiCart,
  shopConfig,
  sessionUser,
  bootstrapBreakpoint,
}) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isShowingTooltip, setIsShowingTooltip] = useState(false);

  const { cartUpdateMessage } = persistedMultiCart;

  const firstRender = useRef(true);
  useLayoutEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (cartUpdateMessage) {
      setIsShowingTooltip(true);
      keyedDebounceSingleton.debounce({
        queue: `shopNavbar${bootstrapBreakpoint}`,
        key: 'delayedClosing',
        delayedFunction: () => {
          setIsShowingTooltip(false);
        },
        delay: 3141,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedMultiCart]);

  if (_.isEmpty(sessionUser)) return <></>;

  const toggleModal = () => {
    setIsCartOpen(!isCartOpen);
  };
  const totalNumberOfArticles = _.map(persistedMultiCart.carts, cart =>
    cart.cartRows.map(x => x.pricedArticle.id),
  ).flat().length;
  return (
    <>
      <Button
        onClick={toggleModal}
        color="none"
        style={{ width: '30px', height: '30px' }}
        id={`ShoppingCartButton${bootstrapBreakpoint}`}
        className="p-0 me-2 list-inline-item position-relative ms-2"
      >
        <IconCart stroke={isPrivabShop(shopConfig) ? 'white' : 'black'} />
        <div
          className={`navbar-icon-badge cart-badge ${
            isPrivabShop(shopConfig)
              ? 'bg-light text-dark'
              : 'bg-dark text-light'
          }`}
        >
          <strong>{totalNumberOfArticles}</strong>
        </div>

        <Tooltip
          className={`
            shopping-cart-tooltip ${
              bootstrapBreakpoint === 'lg'
                ? 'd-none d-lg-flex'
                : 'd-flex d-lg-none'
            }`}
          key={`cartTooltip${bootstrapBreakpoint}`}
          placement="bottom"
          isOpen={isShowingTooltip}
          target={`ShoppingCartButton${bootstrapBreakpoint}`}
        >
          <div>{cartUpdateMessage}</div>
        </Tooltip>
      </Button>
      <CartOffCanvas isOpen={isCartOpen} toggle={toggleModal} />
    </>
  );
};
ShoppingCartButton.propTypes = {
  persistedMultiCart: PropTypes.object.isRequired,
  shopConfig: PropTypes.object.isRequired,
  sessionUser: PropTypes.object,
  bootstrapBreakpoint: PropTypes.string,
};

const ProfileButton = ({ sessionUser, toggleLoginModal, shopConfig }) => {
  const isLoggedIn = !_.isEmpty(sessionUser);

  const [isUserOffCanvasOpen, setIsUserOffCanvasOpen] = useState(false);
  const toggleUserOffCanvas = () => {
    setIsUserOffCanvasOpen(!isUserOffCanvasOpen);
  };
  return (
    <>
      {isLoggedIn && (
        <>
          <Button
            id="ProfileButton"
            onClick={isLoggedIn ? toggleUserOffCanvas : toggleLoginModal}
            className="p-0 list-inline-item position-relative ms-2"
            name="profile"
            color="none"
            style={{ width: '30px', height: '30px' }}
          >
            {sessionUser && (
              <IconProfile
                fill="none"
                stroke={isPrivabShop(shopConfig) ? 'white' : 'black'}
              />
            )}
          </Button>

          {!isLoggedIn && <LoginModal />}
          {isLoggedIn && (
            <UserOffCanvas
              isOpen={isUserOffCanvasOpen}
              toggle={toggleUserOffCanvas}
            />
          )}
        </>
      )}
    </>
  );
};

ProfileButton.propTypes = {
  sessionUser: PropTypes.object,
  toggleLoginModal: PropTypes.func.isRequired,
};
ProfileButton.defaultProps = {
  sessionUser: null,
};

const LogInButton = ({ authentication, sessionUser, toggleLoginModal }) => {
  const isLoggedIn = !_.isEmpty(sessionUser);
  const isAuthenticated = authentication.isAuthenticated;

  const [isUserOffCanvasOpen, setIsUserOffCanvasOpen] = useState(false);
  const toggleUserOffCanvas = () => {
    setIsUserOffCanvasOpen(!isUserOffCanvasOpen);
  };
  return (
    <>
      {!isAuthenticated && (
        <StyledButton
          id="LogInButtonNavBar"
          onClick={isLoggedIn ? toggleUserOffCanvas : toggleLoginModal}
          className="ms-1 ms-lg-2 p-2"
          color="light"
          outline
        >
          Logga in
        </StyledButton>
      )}

      {!isLoggedIn && <LoginModal />}
      {isLoggedIn && (
        <UserOffCanvas
          isOpen={isUserOffCanvasOpen}
          toggle={toggleUserOffCanvas}
        />
      )}
    </>
  );
};

LogInButton.propTypes = {
  sessionUser: PropTypes.object,
  toggleLoginModal: PropTypes.func.isRequired,
  authentication: PropTypes.object,
};
LogInButton.defaultProps = {
  sessionUser: null,
};

const BecomeCustomerBtn = ({ authentication, shopConfig }) => {
  const isAuthenticated = authentication.isAuthenticated;
  return (
    <>
      {!isAuthenticated &&
        !shopConfig.navbarConfig?.hideBecomeCustomerButton && (
          <StyledButton
            color="light"
            className="p-2"
            id="BecomeCustomerButtonNavBar"
            onClick={() => Router.push('/register')}
            outline
          >
            Bli kund
          </StyledButton>
        )}
    </>
  );
};

BecomeCustomerBtn.propTypes = {
  authentication: PropTypes.object.isRequired,
};

function NavBar({ asPath, authentication, shopConfig, isAtTopOfPage }) {
  const [isSearchbarFocused, setIsSearchbarFocused] = useState(false);
  const { isHamburgerMenuOpen, toggleHamburgerMenu } = useHamburgerMenu();
  const { isAuthenticated } = authentication;

  const closeFocus = () => {
    setIsSearchbarFocused(false);
  };

  useEffect(() => {
    if (isSearchbarFocused) {
      document.getElementById('navbar').classList.add('search-focused');
    } else {
      document.getElementById('navbar').classList.remove('search-focused');
    }
  }, [isSearchbarFocused]);

  return (
    <>
      <Navbar
        id="navbar"
        container="xxl"
        className={`d-print-none sticky-top navbar-version-2 px-0 ${
          isAtTopOfPage ? 'is-at-top-of-page' : ''
        }`}
      >
        {isSearchbarFocused && (
          <div
            style={{
              background: 'rgba(0,0,0,0.3)',
              width: '100vw',
              height: '170vh',
              zIndex: '1030',
              top: 0,
              left: 0,
              position: 'absolute',
            }}
            onClick={() => setIsSearchbarFocused(false)}
          ></div>
        )}
        <OnlyOnSmallScreen>
          <Row className="w-100 align-items-center g-0">
            <Col xs={5} className="align-items-center">
              <ConnectedLogo />
            </Col>
            <Col xs={7} className="text-nowrap text-end pe-2">
              <span className="pe-2">
                {!isAuthenticated && (
                  <>
                    <ConnectedBecomeCustomerBtn />
                    <ConnectedLogInButton />
                  </>
                )}
                <ConnectedCartButton bootstrapBreakpoint="xs" />
                <MyFavoritesButton />
              </span>

              <NavbarToggler onClick={toggleHamburgerMenu}>
                <IconHambugerMenu
                  stroke={isPrivabShop(shopConfig) ? 'white' : 'black'}
                />
              </NavbarToggler>
            </Col>
          </Row>
          <Row>
            <Col
              xs={12}
              className="d-flex justify-content-start align-items-center"
            >
              <div
                className="search-bar-container"
                style={
                  isSearchbarFocused
                    ? {
                        zIndex: '5000',
                      }
                    : {}
                }
                onClick={() => setIsSearchbarFocused(true)}
              >
                <SearchInput
                  closeFocus={closeFocus}
                  hideSearchIcon
                  shopConfig={shopConfig}
                />
              </div>
            </Col>
          </Row>
        </OnlyOnSmallScreen>
        <OnlyOnLargeScreen>
          <Row className="g-0 w-100 justify-content-between flex-nowrap">
            <Col lg="auto" className="align-items-center">
              <ConnectedLogo />
            </Col>
            <Col lg="auto" className="d-none d-lg-flex align-items-center">
              <PageNavigation
                toggleHamburgerMenu={toggleHamburgerMenu}
                isHamburgerMenuOpen={isHamburgerMenuOpen}
                asPath={asPath}
              />
            </Col>
            <Col
              lg="auto"
              className="d-flex justify-content-end align-items-center pe-2"
            >
              <div
                className="search-bar-container"
                style={
                  isSearchbarFocused
                    ? {
                        zIndex: '5000',
                      }
                    : {}
                }
                onClick={() => setIsSearchbarFocused(true)}
              >
                <SearchInput
                  closeFocus={closeFocus}
                  hideSearchIcon
                  shopConfig={shopConfig}
                />
              </div>
            </Col>
            <Col lg="auto" className="text-nowrap d-flex align-items-center">
              <div>
                {!isAuthenticated && (
                  <>
                    <ConnectedBecomeCustomerBtn />
                    <ConnectedLogInButton />
                  </>
                )}
                <ConnectedProfileButton />
                <ConnectedCartButton bootstrapBreakpoint="lg" />
                <MyFavoritesButton />
              </div>
            </Col>
          </Row>
        </OnlyOnLargeScreen>
        <OnlyOnSmallScreen>
          <MobileOffCanvas
            isOpen={isHamburgerMenuOpen}
            toggle={toggleHamburgerMenu}
            asPath={asPath}
            shopConfig={shopConfig}
          />
        </OnlyOnSmallScreen>
      </Navbar>
    </>
  );
}

const mapStateToProps = ({
  shopConfig,
  authentication,
  persistedMultiCart,
  myFavoriteArticles,
}) => ({
  shopConfig,
  sessionUser: authentication.sessionUser,
  authentication,
  persistedMultiCart,
  myFavoriteArticles,
});

const mapDispatchToProps = {
  toggleLoginModal: _toggleLoginModal,
};
NavBar.propTypes = {
  asPath: PropTypes.string.isRequired,
  authentication: PropTypes.object.isRequired,
  shopConfig: PropTypes.object.isRequired,
};
const ConnectedBecomeCustomerBtn = connect(mapStateToProps)(BecomeCustomerBtn);
const ConnectedLogo = connect(mapStateToProps)(Logo);
const ConnectedCartButton = connect(mapStateToProps)(ShoppingCartButton);
const ConnectedProfileButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProfileButton);
const ConnectedLogInButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LogInButton);
export default connect(mapStateToProps)(NavBar);
